import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import Button from '@renderer/components/Button'
import { COUNTDOWN_PLACEHOLDER, useCountdown } from '@renderer/utils/countdownUtils'
import { useTranslation } from 'react-i18next'
import { FrameOption } from '@renderer/types/configState'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useBackgroundTextColor } from '@renderer/hooks/useBackgroundTextColor'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'

const FramePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    frames: allFrames,
    frameCategories,
    config: globalConfig,
    getPageSettings
  } = useConfigStore()
  const config = globalConfig?.pages.frame
  const { primaryColor, secondaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const { setSessionState, logActivity } = useSessionStateStore()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const {
    isPortrait: isPotrait,
    isCompact: compactFrameLayout,
    designW,
    designH,
    shortSide
  } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  const viewportLongSide = Math.max(designW, designH)
  /** Physical phone/tablet in landscape — many columns fit; design canvas may still be “portrait” */
  const frameMobileLandscape = viewportCompact && isLandscapeViewport
  /** Compact + design portrait: preview on top, grid below */
  const stackPreviewTop = compactFrameLayout && isPotrait
  const frameLandscapeCompact = compactFrameLayout && !isPotrait
  /**
   * Large landscape row layout: 4 columns only for very wide canvases (>2400px).
   * Standard 1920 designs stay at 3 cols for a premium, uncrowded feel (matching legacy).
   */
  const frameGridLargeLandscape =
    !stackPreviewTop &&
    isLandscapeViewport &&
    viewportLongSide > 2400 &&
    !compactFrameLayout
  /** Row preview pane: same default Button sizing as Select / Filter (no full-width bar). */
  const frameNextRowBtn = compactFrameLayout ? '!px-4 !py-2 !text-sm' : undefined
  /**
   * Stacked mobile portrait: floating Next — match Select.tsx selectNextStackedBtn exactly.
   */
  const frameNextStackedBtn =
    compactFrameLayout && frameMobileLandscape
      ? '!inline-flex !h-9 !min-h-9 !w-auto !max-w-[9.5rem] !shrink-0 !items-center !justify-center !px-3 !py-1.5 !text-xs !leading-snug'
      : compactFrameLayout
        ? '!inline-flex !h-10 !min-h-10 !w-auto !max-w-[11rem] !shrink-0 !items-center !justify-center !px-4 !py-2 !text-sm !leading-snug'
        : '!inline-flex !min-h-[3.25rem] !w-auto !max-w-[14rem] !items-center !justify-center !px-6 !py-3 !text-lg !leading-snug'
  /** Thumbnail fits canvas aspect inside max area without overflowing */
  const framePreviewAspectBox =
    stackPreviewTop ||
    frameLandscapeCompact ||
    (!stackPreviewTop && isLandscapeViewport)
  /**
   * Full-size landscape row (e.g. 1920×1080): default preview pane is max-w-[35%] + p-8, which
   * crowds the template grid — use a slightly narrower pane and padding.
   */
  const framePreviewPaneLandscapeTight =
    isLandscapeViewport &&
    !compactFrameLayout &&
    !stackPreviewTop &&
    !frameLandscapeCompact &&
    !isPotrait &&
    shortSide <= 1200
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const welcomeSettings = getPageSettings('welcome')
  const isCategoryModeEnabled = config?.settings?.categoryModeEnabled || false
  const hasBackgroundImage = Boolean(globalConfig?.theme?.backgroundImageUrl)
  const textColor = useBackgroundTextColor()


  const nextPage = welcomeSettings?.startWithPayment ? 'capture' : 'checkout'

  const [currentFrames, setCurrentFrames] = useState<FrameOption[]>([])
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)

  // Calculate frame aspect ratio for layout adjustments
  const selectedFrameData = allFrames.find((f) => f._id === selectedFrame)
  const frameAspectRatio = selectedFrameData
    ? selectedFrameData.drawParams.canvasWidth / selectedFrameData.drawParams.canvasHeight
    : 1
  const isTallFrame = frameAspectRatio < 0.5 // For frames like 2R (500x1500 = 0.33)

  // Initialize countdown with the useCountdown hook
  const frameTimeout = config?.settings?.frameTimeout || 0
  const { setOnTick, setOnComplete, start, stop, getFormattedRemainingSeconds } =
    useCountdown(frameTimeout)

  useEffect(() => {
    let initialFramesToDisplay: FrameOption[] = []

    if (isCategoryModeEnabled && frameCategories.length > 0) {
      // Set initial selected category to the first one
      setSelectedCategory(frameCategories[0].name)
      initialFramesToDisplay = frameCategories[0].frames || []
    } else {
      // Fallback to 'all' if not in category mode or no categories
      setSelectedCategory('all')
      initialFramesToDisplay = allFrames
    }
    setCurrentFrames(initialFramesToDisplay)

    if (initialFramesToDisplay.length > 0) {
      setSelectedFrame(initialFramesToDisplay[0]._id)
    }
  }, [isCategoryModeEnabled, frameCategories, allFrames])

  useEffect(() => {
    if (isCategoryModeEnabled && selectedCategory !== 'all') {
      const category = frameCategories.find((cat) => cat.name === selectedCategory)
      setCurrentFrames(category?.frames || [])
    } else if (!isCategoryModeEnabled) {
      // In non-category mode or 'all' category selected, use all frames
      setCurrentFrames(allFrames)
    }
  }, [isCategoryModeEnabled, selectedCategory, frameCategories, allFrames])

  // When switching to a category with no frames, keep the last selected frame for preview (avoid empty / invalid src).
  // When the grid has frames, ensure selection is valid for that list.
  useEffect(() => {
    if (currentFrames.length === 0) {
      return
    }
    if (!selectedFrame || !currentFrames.some((f) => f._id === selectedFrame)) {
      setSelectedFrame(currentFrames[0]._id)
    }
  }, [currentFrames, selectedFrame])

  /** Same priority as initial load, so Next works before React effects flush */
  const handleNext = useCallback((): void => {
    const frameId =
      selectedFrame ??
      currentFrames[0]?._id ??
      (isCategoryModeEnabled ? frameCategories[0]?.frames?.[0]?._id : undefined) ??
      allFrames[0]?._id ??
      null
    if (!frameId) return

    const frame = allFrames.find((f) => f._id === frameId)
    if (!frame) return

    if (frameId !== selectedFrame) {
      setSelectedFrame(frameId)
    }

    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId,
        framePhotoRatio: photoRatio
      }
    })
    const frameName = frame.name
    logActivity(`Selected frame: ${frameName || frameId}`, { frameId })

    const next = welcomeSettings?.startWithPayment ? 'capture' : 'checkout'
    navigate(`/${projectId}/${next}`)
  }, [
    selectedFrame,
    currentFrames,
    isCategoryModeEnabled,
    frameCategories,
    allFrames,
    setSessionState,
    logActivity,
    navigate,
    projectId,
    welcomeSettings
  ])

  useEffect(() => {
    if (frameTimeout <= 0) return
    setOnTick(() => {
      setRemainingTime(getFormattedRemainingSeconds())
    })
    setOnComplete(() => {
      logActivity('Frame selection timed out; continuing with default frame', {
        timeoutDuration: frameTimeout
      })
      handleNext()
    })
  }, [
    frameTimeout,
    setOnTick,
    setOnComplete,
    getFormattedRemainingSeconds,
    handleNext,
    logActivity
  ])

  useEffect(() => {
    if (frameTimeout <= 0) return
    start()
    return (): void => {
      stop()
    }
  }, [frameTimeout, start, stop])

  const gridColsClass = stackPreviewTop
    ? 'grid-cols-3 gap-3 items-stretch'
    : frameGridLargeLandscape
      ? 'grid-cols-4 gap-10 items-end'
      : frameMobileLandscape
        ? 'grid-cols-3 gap-3 items-end'
        : frameLandscapeCompact
          ? 'grid-cols-3 gap-4 items-end'
          : isPotrait
            ? 'grid-cols-1 gap-5 items-end'
            : 'grid-cols-3 gap-10 items-end'

  /** Keep category chips smaller than the primary Next control; tighter strip = more tabs visible */
  const categoryStripClass = frameLandscapeCompact
    ? 'gap-1.5 px-2 py-1.5'
    : compactFrameLayout
      ? 'gap-1.5 px-2 py-2'
      : 'gap-4 px-12 py-4'
  const categoryChipClass = frameLandscapeCompact
    ? 'px-2.5 py-1 text-[11px] font-medium'
    : compactFrameLayout
      ? 'px-3 py-1.5 text-xs font-medium'
      : 'px-5 py-2 text-base font-medium'

  const categoryScrollStrip =
    isCategoryModeEnabled && frameCategories && frameCategories.length > 0 ? (
      <div
        className={`custom-scrollbar flex w-full min-w-0 flex-nowrap justify-start overflow-x-auto overflow-y-hidden ${categoryStripClass}`}
      >
        {frameCategories.map((category) => (
          <button
            type="button"
            key={category.name}
            onClick={() => setSelectedCategory(category.name)}
            className={`shrink-0 rounded-full whitespace-nowrap transition-all ${categoryChipClass} ${
              selectedCategory === category.name ? '' : 'border'
            }`}
            style={
              selectedCategory === category.name
                ? { backgroundColor: primaryColor, color: secondaryColor }
                : { borderColor: textColor, color: textColor }
            }
          >
            {category.name}
          </button>
        ))}
      </div>
    ) : null

  const selectionColumn = (
    <div
      className={`relative flex min-h-0 min-w-0 flex-col ${stackPreviewTop ? 'max-w-full flex-1' : 'max-w-[70%] flex-1'}`}
      style={{ willChange: 'transform' }}
    >
      {categoryScrollStrip &&
        (hasBackgroundImage ? (
          <div className="w-full min-w-0 shrink-0 shadow-xl">{categoryScrollStrip}</div>
        ) : (
          <div className="w-full min-w-0 shrink-0 shadow-sm">{categoryScrollStrip}</div>
        ))}

      <div
        className={`custom-scrollbar min-h-0 flex-1 overflow-y-auto ${
          stackPreviewTop
            ? 'mt-1.5 px-2 pb-14 pt-2'
            : frameLandscapeCompact
              ? 'mt-1.5 px-1.5 py-1.5'
              : compactFrameLayout
                ? 'mt-2 px-3 py-3'
                : 'mt-8 px-12 py-8'
        }`}
      >
        <div className={`grid ${gridColsClass}`} role="listbox" title={t('frame.framesListbox', 'Frames')}>
          {currentFrames.map((frame) => (
            <button
              role="option"
              aria-label={frame.name}
              type="button"
              key={frame._id}
              onClick={() => setSelectedFrame(frame._id)}
              className={`flex min-w-0 cursor-pointer flex-col items-stretch text-center transition-all ${
                frameMobileLandscape
                  ? 'p-1'
                  : frameLandscapeCompact
                    ? 'p-1.5'
                    : compactFrameLayout
                      ? 'p-2'
                      : 'p-6'
              } ${selectedFrame === frame._id ? 'scale-105' : ''}`}
            >
              <img
                crossOrigin="anonymous"
                src={frame.thumbnail}
                alt={frame.name}
                className={`w-full min-h-0 max-w-full rounded-lg object-contain shadow-xl ${
                  selectedFrame === frame._id ? 'border-4' : 'border border-gray-100'
                }`}
                style={
                  selectedFrame === frame._id ? { borderColor: primaryColor } : undefined
                }
              />
              {!stackPreviewTop && (
                <p
                  className={`line-clamp-2 min-h-0 w-full max-w-full break-words ${
                    frameMobileLandscape || (isLandscapeViewport && compactFrameLayout)
                      ? 'mt-1 text-[10px] leading-tight'
                      : compactFrameLayout
                        ? 'mt-1 text-xs leading-snug'
                        : 'mt-2 text-xl leading-snug'
                  }`}
                  style={{ color: textColor }}
                  title={frame.name}
                >
                  {frame.name}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const previewPanel = (
    <GlassPanel
      className={`flex min-h-0 min-w-0 w-full flex-col items-center ${
        stackPreviewTop
          ? 'max-h-[45%] max-w-full shrink-0 overflow-hidden p-2'
          : frameLandscapeCompact
            ? 'h-full max-w-[40%] flex-1 overflow-hidden p-2'
            : isPotrait
              ? `h-full max-w-[55%] overflow-hidden ${compactFrameLayout ? 'p-8' : 'p-12'}`
              : framePreviewPaneLandscapeTight
                ? 'h-full max-w-[30%] min-w-0 overflow-hidden p-6'
                : 'h-full max-w-[35%] overflow-hidden p-8'
      }`}
    >
      <div
        className={`flex w-full min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden ${
          framePreviewAspectBox
            ? framePreviewPaneLandscapeTight
              ? 'px-0.5 py-0.5'
              : 'px-1 py-1'
            : 'px-2'
        } ${isTallFrame && !framePreviewAspectBox ? 'custom-scrollbar overflow-y-auto' : ''}`}
      >
        {selectedFrameData && framePreviewAspectBox ? (
          <img
            src={selectedFrameData.thumbnail || ''}
            alt={t('frame.previewAlt', 'Preview')}
            crossOrigin="anonymous"
            className={`max-h-full max-w-full rounded-lg object-contain object-center drop-shadow-xl ${
              framePreviewPaneLandscapeTight ? 'max-h-[min(100%,92%)]' : ''
            }`}
          />
        ) : (
          <div
            className={`px-4 ${
              compactFrameLayout
                ? 'w-full max-w-[320px]'
                : `${isTallFrame ? 'w-[300px]' : 'w-[426px]'} shrink-0`
            }`}
          >
            <img
              src={selectedFrameData?.thumbnail || ''}
              alt={t('frame.previewAlt', 'Preview')}
              crossOrigin="anonymous"
              className="w-full object-contain drop-shadow-xl"
            />
          </div>
        )}
      </div>

      <div
        className={`flex shrink-0 flex-col gap-2 pt-2 ${
          stackPreviewTop ? 'items-center' : 'w-full min-w-0 items-center justify-center px-2 sm:px-3'
        }`}
      >
        {stackPreviewTop && selectedFrameData && (
          <p
            className="line-clamp-2 max-w-full px-2 text-center text-xs leading-tight text-carbon/90"
            title={selectedFrameData.name}
          >
            {selectedFrameData.name}
          </p>
        )}
        {(config?.settings?.frameTimeout || 0) > 0 && (
          <span
            className={`self-center font-mono tabular-nums text-carbon ${compactFrameLayout ? 'text-lg' : 'text-2xl'}`}
          >
            {remainingTime}
          </span>
        )}
        {!stackPreviewTop && (
          <Button
            text={t('common.next', 'Next')}
            onClick={handleNext}
            data-nav-to={nextPage}
            className={frameNextRowBtn}
          />
        )}
      </div>
    </GlassPanel>
  )

  return (
    <BackgroundContainer
      className={`relative flex h-full min-h-0 ${stackPreviewTop ? 'flex-col' : 'flex-row'}`}
      data-page="frame"
    >
      {stackPreviewTop ? (
        <>
          {previewPanel}
          {selectionColumn}
        </>
      ) : (
        <>
          {selectionColumn}
          {previewPanel}
        </>
      )}
      {stackPreviewTop && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[100] flex justify-end p-0">
                <div className="pointer-events-auto pr-4 pb-12">
            <Button
              text={t('common.next', 'Next')}
              onClick={handleNext}
              data-nav-to={nextPage}
              className={`${frameNextStackedBtn} shadow-lg`}
            />
          </div>
        </div>
      )}
    </BackgroundContainer>
  )
}

export default FramePage
